import type { GetPagesQuery } from '@seek/cmsu-cms-connect';
import {
  type Locale,
  type SiteNamesMatchingClassification,
  siteNames,
} from '@seek/melways-sites';

export type Environment = 'development' | 'staging' | 'production';

export type EmployerSiteName = SiteNamesMatchingClassification<{
  product: 'employer';
}>;

export type AuthenticationStatus = 'unauthenticated' | 'pending';

export enum ModuleTheme {
  LIGHT = 'light',
  MEDIUM = 'medium',
  DARK = 'dark',
}

export interface ShopfrontConfig {
  metricsHost: string;
  site: EmployerSiteName;
  locale: Locale;
  language: string;
  brand: string;
  country: string;
  zone: string;
  environment: Environment;
  branch?: string;
  version?: string;
}

export interface PageData {
  page: string;
  siteSection: string;
  loginStatus: string;
}

export interface AdvertiserRegistrationDetails {
  businessName: string;
  phoneNumber: string;
}

export interface UserRegistrationDetails {
  firstName: string;
  lastName: string;
  email: string;
}

export interface RegistrationDetails
  extends UserRegistrationDetails,
    AdvertiserRegistrationDetails {}

export const isValidConfigType = (config: {
  site: unknown;
  locale: unknown;
  environment: unknown;
}): config is Pick<ShopfrontConfig, 'site' | 'locale' | 'environment'> =>
  isEmployerSiteName(config.site) &&
  isLocale(config.locale) &&
  isEnvironment(config.environment);

export const isEmployerSiteName = (site: unknown): site is EmployerSiteName =>
  // @ts-expect-error
  Object.values(siteNames.employer.seek).includes(site);

const locales: Locale[] = [
  'en-AU',
  'en-NZ',
  'en-HK',
  'en-TH',
  'en-SG',
  'en-ID',
  'en-PH',
  'en-MY',
  'th-TH',
  'id-ID',
];
export const isLocale = (locale: unknown): locale is Locale =>
  // @ts-expect-error
  locales.includes(locale);

const environments: Environment[] = ['development', 'production', 'staging'];
export const isEnvironment = (
  environment: unknown,
): environment is Environment =>
  // @ts-expect-error
  environments.includes(environment);

export type CmsPageContent = GetPagesQuery['pages'][0];
export type PageContentItems = Record<string, CmsPageContent>;

export type SecureLinkErrorType = 'expired' | 'invalid' | 'used';

export type SecureLinkValidationResponse =
  | {
      validationResult: 'success';
      redirectUri: string;
    }
  | {
      validationResult: SecureLinkErrorType;
    };

export type SecureLinkFlow =
  | 'completeUserRegistration'
  | 'CompleteAdditionalAdvertiserRegistration';

export type Maybe<T> = T | null;
